import clsx from "clsx";
import { get, throttle } from "lodash-es";
import Delta from "quill-delta";
import { FC, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { Doc } from "sharedb/lib/client";

import type { PresenceViewShortType } from "../../../../shared/types/sharedb";
import type { PathType, WorksheetType } from "../../../../shared/types/worksheet";

import {
  ProseMirrorEditorInstance,
  type ProseMirrorEditorInstanceOptions,
} from "./prosemirror/editor-instance";

import { UserType } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { USER_INPUT_EVENT_CATEGORY, addTrackingToEvent, getPageFieldProps } from "utils/amplitude";

const STICKY_STATE_THROTTLE_MS = 50;
const STICKY_BOTTOM_CONTROLS_TOP_GAP_PX = 100;
const STICKY_BOTTOM_CONTROLS_HEIGHT_CSS_VARIABLE = "--sticky-rich-text-bottom-controls-height";

function setupBottomControlsPositioning(editorElement: HTMLElement): () => void {
  const editorWrapper = editorElement.closest<HTMLElement>(".prosemirror-editor-wrapper");
  const scrollContainer = editorElement.closest<HTMLElement>(
    '[data-rich-text-scroll-container="true"]',
  );
  const bottomControlsElement = editorElement.querySelector<HTMLElement>(
    ".prosemirror-bottom-controls",
  );

  if (!editorWrapper || !scrollContainer || !bottomControlsElement) return () => undefined;

  let isSticky = false;
  let publishedBottomControlsHeight: number | null = null;
  let isTracking = false;
  let clearStickyTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelPendingClearSticky = () => {
    if (clearStickyTimer !== null) {
      clearTimeout(clearStickyTimer);
      clearStickyTimer = null;
    }
  };

  const clearStickyState = () => {
    bottomControlsElement.removeAttribute("data-sticky");
    isSticky = false;

    if (publishedBottomControlsHeight !== null && publishedBottomControlsHeight !== 0) {
      scrollContainer.style.setProperty(STICKY_BOTTOM_CONTROLS_HEIGHT_CSS_VARIABLE, "0px");
      publishedBottomControlsHeight = 0;
    }
  };

  const updateStickyState = () => {
    if (!editorWrapper.matches(":focus-within")) {
      clearStickyState();
      return;
    }

    const editorRect = editorWrapper.getBoundingClientRect();
    const scrollerRect = scrollContainer.getBoundingClientRect();
    const bottomControlsHeight = bottomControlsElement.getBoundingClientRect().height;
    const stickyBottomControlsTop = scrollerRect.bottom - bottomControlsHeight;
    const nextIsSticky =
      stickyBottomControlsTop - editorRect.top >= STICKY_BOTTOM_CONTROLS_TOP_GAP_PX &&
      editorRect.bottom > scrollerRect.bottom;

    if (nextIsSticky !== isSticky) {
      if (nextIsSticky) {
        bottomControlsElement.setAttribute("data-sticky", "true");
      } else {
        bottomControlsElement.removeAttribute("data-sticky");
      }
      isSticky = nextIsSticky;
    }

    const nextBottomControlsHeight = nextIsSticky ? bottomControlsHeight : 0;
    if (nextBottomControlsHeight !== publishedBottomControlsHeight) {
      scrollContainer.style.setProperty(
        STICKY_BOTTOM_CONTROLS_HEIGHT_CSS_VARIABLE,
        `${nextBottomControlsHeight}px`,
      );
      publishedBottomControlsHeight = nextBottomControlsHeight;
    }
  };

  const throttledUpdateStickyState = throttle(updateStickyState, STICKY_STATE_THROTTLE_MS, {
    leading: true,
    trailing: true,
  });
  const resizeObserver = new ResizeObserver(throttledUpdateStickyState);

  const startTracking = () => {
    cancelPendingClearSticky();
    if (isTracking) return;

    isTracking = true;
    scrollContainer.addEventListener("scroll", throttledUpdateStickyState);
    resizeObserver.observe(editorWrapper);
    resizeObserver.observe(scrollContainer);
    resizeObserver.observe(bottomControlsElement);
    throttledUpdateStickyState();
  };

  const stopTracking = (options?: { clearStickyImmediately?: boolean }) => {
    cancelPendingClearSticky();

    if (isTracking) {
      isTracking = false;
      scrollContainer.removeEventListener("scroll", throttledUpdateStickyState);
    }

    resizeObserver.disconnect();
    throttledUpdateStickyState.cancel();

    if (options?.clearStickyImmediately) {
      clearStickyState();
      return;
    }

    // Clicking a page button blurs the editor (focusout) before click. Clearing sticky here resets
    // --sticky-rich-text-bottom-controls-height immediately, which animates PageButtons down and
    // moves the target out from under the pointer so click never fires. Defer to the next task.
    clearStickyTimer = setTimeout(() => {
      clearStickyTimer = null;
      clearStickyState();
    }, 0);
  };

  const handleFocusIn = () => startTracking();
  const handleFocusOut = (event: FocusEvent) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (!relatedTarget || !editorWrapper.contains(relatedTarget)) stopTracking();
  };

  editorElement.addEventListener("focusin", handleFocusIn);
  editorElement.addEventListener("focusout", handleFocusOut);
  if (editorWrapper.matches(":focus-within")) startTracking();

  return () => {
    editorElement.removeEventListener("focusin", handleFocusIn);
    editorElement.removeEventListener("focusout", handleFocusOut);
    stopTracking({ clearStickyImmediately: true });
  };
}

export interface ProseMirrorPortalProps {
  containerElement: HTMLElement | null; // portal render target
  id?: string;
  className?: string;
  showPlaceholder: boolean;
  placeHolderText: string;
  readOnly: boolean;
  doc: Doc<WorksheetType>; // ShareDB document
  path: PathType; // path to the field's data specifically
  fieldPath?: PathType; // path to entire field definition (e.g. to read metadata like maxLength)
  userId?: string | number; // for ShareDB presence
  presenceViewType?: PresenceViewShortType;
  users: Record<string, UserType>; // section user map for presence cursor labels
  eventName?: string;
  pageId: string; // for error and debug logs
  embedded?: boolean; // renders inside another component
  create?: boolean; // create doc[path] if it doesn't exist yet
  characterCounter: "NONE" | "READ" | "EDIT";
  maxLength?: number | null;
  isAuthoringPage?: boolean;
  onEmbedURLFromPaste?: (url: string) => void; // called when a pasted URL is detected
  onEmbedURLFromLink?: (url: string) => void; // called when a link is added via toolbar; block-level fields only
}

/**
 * Renders the editor UI through a React portal into the container element provided by the parent.
 * A portal is used because ProseMirror manages its own DOM directly, which would conflict with
 * React's reconciliation if they shared the same subtree.
 */
export const ProseMirrorPortal: FC<ProseMirrorPortalProps> = ({
  containerElement,
  id,
  className = "",
  readOnly,
  doc,
  path,
  fieldPath,
  userId,
  presenceViewType,
  users,
  eventName,
  pageId,
  embedded = false,
  create = false,
  showPlaceholder = false,
  placeHolderText = "",
  characterCounter,
  maxLength,
  isAuthoringPage,
  onEmbedURLFromPaste,
  onEmbedURLFromLink,
}) => {
  const rootElementRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<ProseMirrorEditorInstance | null>(null);
  const { canAuthorSection } = useCapabilities();
  const hasContainer = containerElement !== null;

  useEffect(() => {
    if (!containerElement || !rootElementRef.current) return;

    const options: ProseMirrorEditorInstanceOptions = {
      doc,
      path,
      readOnly,
      userId,
      canAuthorSection,
      presenceViewType,
      users,
      embedded,
      create,
      pageId,
      showPlaceholder,
      placeHolderText,
      characterCounter,
      fieldPath,
      maxLength,
      isAuthoringPage,
      onEmbedURLFromPaste,
      onEmbedURLFromLink,
    };
    const instance = new ProseMirrorEditorInstance(rootElementRef.current, options);
    editorInstanceRef.current = instance;

    const editorElement = rootElementRef.current;
    const editorWrapper = editorElement.closest<HTMLElement>(".prosemirror-editor-wrapper");
    const cleanupBottomControlsPositioning =
      !readOnly && !embedded ? setupBottomControlsPositioning(editorElement) : null;

    const trackedFocus = addTrackingToEvent({
      eventName: eventName || "Free response block selected",
      eventProperties: { ...getPageFieldProps(pageId, path), ...USER_INPUT_EVENT_CATEGORY },
    });

    // The browser can natively focus a contenteditable element for clicks that land
    // outside its bounding box, with no JS involvement. We detect this by tracking
    // whether the preceding user gesture landed inside the editor wrapper. The reset
    // runs in setTimeout(0) so the flag only gates the synchronous focusin that may
    // follow this gesture, without affecting later keyboard or programmatic focus.
    let shouldAcceptFocus = true;
    const markGestureForFocus = (inWrapper: boolean) => {
      shouldAcceptFocus = inWrapper;
      setTimeout(() => {
        shouldAcceptFocus = true;
      }, 0);
    };

    const handleDocMouseDown = (e: MouseEvent) => {
      const inWrapper = !!editorWrapper?.contains(e.target as Node);
      markGestureForFocus(inWrapper);
    };

    // iOS workaround: with viewport zoom enabled (`maximum-scale=3.0`), the synthetic
    // mousedown/focusin/click chain gets dropped on the first tap into a contenteditable
    // that was just blurred. Without this handler, refocusing the editor would require two taps.
    // `pointerup` is still dispatched and counts as a trusted user gesture, so we use it
    // to mark the gesture and explicitly focus the editor at the tap point.
    const handleDocPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch" && e.pointerType !== "pen") return;

      const inWrapper = !!editorWrapper?.contains(e.target as Node);
      markGestureForFocus(inWrapper);
      if (!inWrapper) return;

      // Skip when already focused: the natural mouse chain does fire here and will
      // place the cursor. Running focusAtCoords too would dispatch a redundant selection.
      const alreadyFocused = !!editorWrapper?.contains(document.activeElement);
      if (alreadyFocused) return;

      instance.focusAtCoords(e.clientX, e.clientY);
      instance.scrollCursorAboveKeyboard();
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (!shouldAcceptFocus) {
        (document.activeElement as HTMLElement | null)?.blur();
        return;
      }

      const relatedTarget = e.relatedTarget as Node | null;
      if (!relatedTarget || !editorWrapper?.contains(relatedTarget)) {
        instance.resetToolbarScroll();
      }

      trackedFocus(e);
    };

    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (!relatedTarget || !editorWrapper?.contains(relatedTarget)) {
        instance.resetToolbarPanel();
      }
    };

    const handleBottomControlsMouseDown = (e: MouseEvent) => {
      const target = e.target;
      const editor = editorElement.querySelector<HTMLElement>(".ProseMirror");
      if (
        target instanceof Element &&
        target.closest(".prosemirror-bottom-controls") &&
        !target.closest("input") // allow interaction with character limit input
      ) {
        if (editor && !editor.contains(document.activeElement)) editor.focus();
        e.preventDefault(); // prevents the editor from losing focus/selection on toolbar clicks
      }
    };

    const handleToolbarEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && (e.target as HTMLElement).closest(".rich-text-toolbar")) {
        e.preventDefault();
        if (!instance.dismissToolbarPanel()) {
          editorElement.querySelector<HTMLElement>(".ProseMirror")?.focus();
        }
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown, true);
    document.addEventListener("pointerup", handleDocPointerUp, true);
    editorElement.addEventListener("focusin", handleFocusIn);
    editorElement.addEventListener("focusout", handleFocusOut);
    editorElement.addEventListener("mousedown", handleBottomControlsMouseDown);
    editorElement.addEventListener("keydown", handleToolbarEscape);

    return () => {
      document.removeEventListener("mousedown", handleDocMouseDown, true);
      document.removeEventListener("pointerup", handleDocPointerUp, true);
      editorElement.removeEventListener("focusin", handleFocusIn);
      editorElement.removeEventListener("focusout", handleFocusOut);
      editorElement.removeEventListener("mousedown", handleBottomControlsMouseDown);
      editorElement.removeEventListener("keydown", handleToolbarEscape);
      cleanupBottomControlsPositioning?.();
      instance.destroy();
      editorInstanceRef.current = null;
    };
    // We should take care to avoid recreating the editor unnecessarily.
    // Dependencies not included: containerElement, doc, path, fieldPath, userId, users, pageId,
    // eventName, className, embedded, create, placeHolderText, characterCounter, maxLength,
    // isAuthoringPage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContainer, readOnly, presenceViewType, showPlaceholder, canAuthorSection]);

  // Timeline view replaces doc between renders, so we pull the new content here
  // Also, ShareDB's automatic error recovery will re-fetch the doc data entirely on error,
  // which this file needs to react to by detecting changes in doc.data.
  useEffect(() => {
    if (editorInstanceRef.current) {
      const data = get(doc.data, path) as Delta;
      editorInstanceRef.current.syncContentsFromDoc(data, path);
    }
  }, [doc, path, doc?.data]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setFieldPath(fieldPath);
    }
  }, [fieldPath]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setIsAuthoringPage(isAuthoringPage ?? false);
    }
  }, [isAuthoringPage]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateCharacterCountLimit(maxLength || null);
    }
  }, [maxLength]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateCharacterCountMode(characterCounter);
    }
  }, [characterCounter]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateOnEmbedURLFromPaste(onEmbedURLFromPaste);
    }
  }, [onEmbedURLFromPaste]);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.updateOnEmbedURLFromLink(onEmbedURLFromLink);
    }
  }, [onEmbedURLFromLink]);

  if (!containerElement) {
    return null;
  }

  return createPortal(
    <div
      className={clsx(
        "prosemirror-editor-wrapper relative flex flex-col rounded-md border border-white",
        className,
        {
          "textfield-disabled": readOnly,
          embedded,
        },
      )}
    >
      {/* This div is where ProseMirror attaches the EditorView — do not render React children here. */}
      <div
        className="prosemirror-editor flex flex-1 flex-col text-black @container [outline:none]"
        id={id}
        ref={rootElementRef}
      />
    </div>,
    containerElement,
  );
};
